'use client'

import React, { useState, useRef, useCallback } from 'react'
import { apiCall, ApiError, useApi } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import type { ImportDiff } from '@lens/types/claude'
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "@excellent/ui"
import { StatStrip, type StatStripItem } from "@excellent/ui"
import { Button } from "@excellent/ui"
import { Popover, PopoverContent, PopoverTrigger } from "@excellent/ui"
import { Calendar } from "@excellent/ui"
import { Alert, AlertDescription, AlertTitle } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { SectionHeading } from "@excellent/ui"
import { Separator } from '@excellent/ui'
import { PageHeader } from "@excellent/ui"
import {
 Download,
 Upload,
 AlertTriangle,
 Loader2,
 CalendarDays,
} from "@excellent/ui/icons"
import { format } from 'date-fns'

interface ExportPreview {
 sessionCount: number
 facetCount: number
 historyEntries: number
 hasStatsCache: boolean
 totalSessionsIndexed: number
}

export default function ExportPage() {
 const [exporting, setExporting] = useState(false)
 const [exportRange, setExportRange] = useState<{ from?: Date; to?: Date }>({})
 const [exportPickerOpen, setExportPickerOpen] = useState(false)
 const [importDiff, setImportDiff] = useState<ImportDiff | null>(null)
 const [importError, setImportError] = useState('')
 const [importLoading, setImportLoading] = useState(false)
 const [dragging, setDragging] = useState(false)
 const fileRef = useRef<HTMLInputElement>(null)

 const rangeFromStr = exportRange.from ? format(exportRange.from, 'yyyy-MM-dd') : ''
 const rangeToStr = exportRange.to ? format(exportRange.to, 'yyyy-MM-dd') : ''

 const exportPickerLabel =
 exportRange.from && exportRange.to
 ? `${format(exportRange.from, 'MMM d')} – ${format(exportRange.to, 'MMM d, yyyy')}`
 : exportRange.from
 ? `${format(exportRange.from, 'MMM d, yyyy')} – …`
 : 'Pick date range (optional)'
 const { data: preview, error: previewError, isLoading: previewLoading } = useApi(
 lens.exportPreview,
 { query: { from: rangeFromStr || undefined, to: rangeToStr || undefined } },
 ) as { data: ExportPreview | undefined; error: unknown; isLoading: boolean }

 const dateFilterActive = Boolean(exportRange.from || exportRange.to)

 const previewStats: StatStripItem[] = [
 {
 title: "Sessions",
 value: (preview?.sessionCount ?? 0).toLocaleString(),
 description: dateFilterActive
 ? 'In selected range'
 : `of ${(preview?.totalSessionsIndexed ?? 0).toLocaleString()} indexed`,
 },
 {
 title: "Facets",
 value: (preview?.facetCount ?? 0).toLocaleString(),
 description: "Linked to exported sessions",
 },
 {
 title: "History rows",
 value: (preview?.historyEntries ?? 0).toLocaleString(),
 description: "Recent entries (up to 10k)",
 },
 {
 title: "Stats cache",
 value: preview?.hasStatsCache ? 'Included' : 'Not found',
 description: "From ~/.claude/ when available",
 },
 ]

 async function handleExport() {
 setExporting(true)
 try {
 const dateRange = rangeFromStr || rangeToStr
 ? { from: rangeFromStr || undefined, to: rangeToStr || undefined }
 : undefined

 const data = await apiCall(lens.exportFull, {
 body: { dateRange },
 })
 const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
 const url = URL.createObjectURL(blob)
 const a = document.createElement('a')
 const date = new Date().toISOString().slice(0, 10)
 a.href = url
 a.download = `ccboard-export-${date}.ccboard.json`
 a.click()
 URL.revokeObjectURL(url)
 } finally {
 setExporting(false)
 }
 }

 async function processFile(file: File) {
 setImportError('')
 setImportDiff(null)
 setImportLoading(true)
 try {
 const text = await file.text()
 const json = JSON.parse(text)
 const diff = await apiCall(lens.importPayload, { body: json }) as ImportDiff
 setImportDiff(diff)
 } catch (e) {
 setImportError(e instanceof ApiError ? e.error : String(e))
 } finally {
 setImportLoading(false)
 }
 }

 const onDrop = useCallback((e: React.DragEvent) => {
 e.preventDefault()
 setDragging(false)
 const file = e.dataTransfer.files[0]
 if (file) processFile(file)
 }, [])

 return (
 <div className="flex flex-col">
 <div className="space-y-6 flex-1">
 <PageHeader title="Export" description="Export your Claude Code data to a file, or merge one back in." />
 {previewError ? (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertTitle>Could not load export preview</AlertTitle>
 <AlertDescription>{String(previewError)}</AlertDescription>
 </Alert>
 ) : null}

 {/* Summary — fills the page so it never feels empty */}
 <section className="space-y-3">
 <SectionHeading
 title="What will be exported"
 description="One JSON file includes stats cache, session metadata, facets, and command history. Numbers below respect the optional date range."
 />
 {previewLoading && !preview ? (
 <Skeleton className="h-[104px] rounded-lg" />
 ) : (
 <StatStrip items={previewStats} variant="standalone" />
 )}
 </section>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:items-stretch">
 {/* Export */}
 <Card className="flex h-full min-h-0 flex-col">
 <CardHeader>
 <CardTitle>Export</CardTitle>
 </CardHeader>
 <CardContent className="flex flex-1 flex-col gap-5 min-h-0">
 <div className="space-y-2">
 <span className="type-body leading-none">Date range (optional)</span>
 <div className="flex flex-wrap items-center gap-2">
 <Popover open={exportPickerOpen} onOpenChange={setExportPickerOpen}>
 <PopoverTrigger asChild>
 <Button
 type="button"
 variant={dateFilterActive ? 'default' : 'outline'}
 size="sm"
 className="gap-2 justify-start"
 >
 <CalendarDays className="w-3.5 h-3.5 shrink-0" />
 <span className="truncate">{exportPickerLabel}</span>
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-0" align="start">
 <Calendar
 mode="range"
 selected={{ from: exportRange.from, to: exportRange.to }}
 onSelect={range => {
 setExportRange({ from: range?.from, to: range?.to })
 if (range?.from && range?.to) setExportPickerOpen(false)
 }}
 disabled={{ after: new Date() }}
 initialFocus
 />
 </PopoverContent>
 </Popover>
 {dateFilterActive && (
 <Button
 type="button"
 variant="ghost"
 size="sm"
 className="text-foreground"
 onClick={() => setExportRange({})}
 >
 Clear
 </Button>
 )}
 </div>
 </div>
 {dateFilterActive && (
 <p className="type-body text-foreground rounded-md bg-muted/50 px-3 py-2 surface-edge">
 Only sessions whose <span className=" text-foreground">start time</span> falls in this
 range are included. Use <span className=" text-foreground">Clear</span> for a full export.
 </p>
 )}
 <div className="mt-auto flex flex-col gap-5">
 <Separator />
 <Button
 className="w-full sm:w-auto"
 size="lg"
 onClick={handleExport}
 disabled={exporting || previewLoading}
 >
 {exporting ? (
 <>
 <Loader2 className="size-4 animate-spin" />
 Preparing download…
 </>
 ) : (
 <>
 <Download className="size-4" />
 Download export
 </>
 )}
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Import */}
 <Card className="flex h-full min-h-0 flex-col">
 <CardHeader>
 <CardTitle>Import / merge</CardTitle>
 </CardHeader>
 <CardContent className="flex flex-1 flex-col gap-4 min-h-0">
 {/* button-ignore: drag-and-drop file dropzone, not an action button */}
 <button
 type="button"
 onDragOver={e => {
 e.preventDefault()
 setDragging(true)
 }}
 onDragLeave={() => setDragging(false)}
 onDrop={onDrop}
 onClick={() => fileRef.current?.click()}
 className={`
 flex w-full flex-1 min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed px-4 py-8 text-center transition-colors
 ${dragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40 hover:bg-muted/40'}
 `}
 >
 <Upload className="size-8 mb-3 text-foreground opacity-80" />
 <p className="type-body text-foreground ">Drop .ccboard.json here</p>
 <p className="type-body text-foreground mt-1">
 or <span className="text-foreground underline underline-offset-2">browse files</span>
 </p>
 <input
 ref={fileRef}
 type="file"
 accept=".json,.ccboard.json,application/json"
 className="hidden"
 onChange={e => {
 const file = e.target.files?.[0]
 if (file) processFile(file)
 }}
 />
 </button>

 {importLoading && (
 <div className="flex items-center gap-2 type-body text-foreground">
 <Loader2 className="size-4 animate-spin" />
 Analyzing file…
 </div>
 )}

 {importError && (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription className=" type-body">{importError}</AlertDescription>
 </Alert>
 )}

 {importDiff && (
 <div className="rounded-lg surface-edge bg-muted/30 p-4 space-y-3 type-body">
 <p className="text-foreground">Merge preview</p>
 <dl className="grid gap-2 text-foreground">
 <div className="flex justify-between gap-4">
 <dt>Sessions in file</dt>
 <dd className=" text-foreground tabular-nums">
 {importDiff.total_in_export}
 </dd>
 </div>
 <div className="flex justify-between gap-4">
 <dt>Already present (skipped)</dt>
 <dd className=" tabular-nums text-foreground">{importDiff.already_present}</dd>
 </div>
 <div className="flex justify-between gap-4">
 <dt>New sessions to add</dt>
 <dd
 className={` tabular-nums ${
 importDiff.new_sessions > 0 ? 'text-foreground ' : 'text-foreground'
 }`}
 >
 {importDiff.new_sessions}
 </dd>
 </div>
 </dl>

 {importDiff.new_sessions === 0 ? (
 <p className="type-body text-foreground pt-1 border-t border-border/60">
 Everything in this file is already in your index. Nothing to merge.
 </p>
 ) : (
 <div className="space-y-2 pt-1 border-t border-border/60">
 <Alert className="border-border bg-muted">
 <AlertTriangle className="h-4 w-4 text-foreground" />
 <AlertDescription className="type-body text-foreground /90">
 Writing merged data to ~/.claude/ is not implemented in this build — this is a preview only.
 </AlertDescription>
 </Alert>
 <div className="max-h-36 overflow-auto rounded-md surface-edge bg-background/50 space-y-1 p-2 type-body text-foreground">
 {importDiff.sessions_to_add.slice(0, 12).map(s => (
 <div key={s.session_id}>
 + {s.session_id.slice(0, 8)}… · {s.start_time.slice(0, 10)} ·{' '}
 {s.project_path?.split('/').slice(-1)[0] ?? '—'}
 </div>
 ))}
 {importDiff.sessions_to_add.length > 12 && (
 <p className="text-foreground pt-1">
 …and {importDiff.sessions_to_add.length - 12} more
 </p>
 )}
 </div>
 </div>
 )}
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 </div>
 )
}
